"use client";

import {
  Box,
  Stack,
  Heading,
  Text,
  Container,
  SimpleGrid,
  HStack,
  useToast,
  FormLabel,
  Switch,
  FormControl,
  useColorModeValue,
} from "@chakra-ui/react";
import { CustomInput } from "../components/Input";
import { CustomButton } from "../components/Buttons";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { API_BASE_URL } from "../components/APIs";
import axios from "axios";
import GeneralLayout from "../layout/GeneralLayout";
import Cookies from "js-cookie";
import { USER_LEVELS } from "../util/data";

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const isSubmitting = useRef(false);

  // Check if user is already logged in
  useEffect(() => {
    const token = Cookies.get("token");
    const uid = Cookies.get("uid");
    const isLoggedIn = Cookies.get("isLoggedin");
    const userLevel = Cookies.get("userLevel");

    if (isLoggedIn === "true" && token && uid && userLevel) {
      navigate("/");
    }
  }, [navigate]);

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (data) => {
      if (isSubmitting.current) return;
      
      isSubmitting.current = true;
      setLoading(true);

      try {
        const response = await axios.post(API_BASE_URL + "/auth/login", data);
        const { status, message, uid, userLevel, token, fname } = response.data;

        setLoading(false);
        isSubmitting.current = false;

        switch (status) {
          case 200:
            await handleSuccessfulLogin({ uid, userLevel, token, message });
            break;
          
          case 210:
            handleVerificationRequired({ uid, fname, email: data.email, message });
            break;
          
          case 220:
            showToast("Site Message", message, "info");
            break;
          
          default:
            showToast("Login Failed", "Invalid email or password", "error");
        }
      } catch (error) {
        setLoading(false);
        isSubmitting.current = false;
        showToast("Login Failed", "Internet Connection Problem", "error");
      }
    },
  });

  const handleSuccessfulLogin = async ({ uid, userLevel, token, message }) => {
    // Store authentication data with proper synchronization
    await storeAuthData({ uid, userLevel, token });
    
    showToast("Site Message", message, "success");
    
    // Wait a bit longer to ensure all storage is committed
    setTimeout(() => {
      navigate("/", { 
        replace: true,
        state: { 
          freshLogin: true,
          userLevel: userLevel,
          timestamp: Date.now()
        }
      });
    }, 300);
  };

  const handleVerificationRequired = ({ uid, fname, email, message }) => {
    localStorage.setItem("email", email);
    localStorage.setItem("uid", uid);
    
    showToast("Site Message", message, "info");
    
    setTimeout(() => {
      navigate("/verify", {
        state: { uid, fname, email },
        replace: true
      });
    }, 100);
  };

  const storeAuthData = async ({ uid, userLevel, token }) => {
    return new Promise((resolve) => {
      // Store in sessionStorage
      sessionStorage.setItem("uid", uid);
      sessionStorage.setItem("userLevel", userLevel);
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("isLoggedin", "true");

      // Store in localStorage for persistence
      localStorage.setItem("userInfo", "true");
      localStorage.setItem("currentUserLevel", userLevel);
      localStorage.setItem("lastLogin", new Date().toISOString());

      // Store in cookies for server-side access
      Cookies.set("token", token, { expires: 1 });
      Cookies.set("uid", uid, { expires: 1 });
      Cookies.set("isLoggedin", "true", { expires: 1 });
      Cookies.set("userLevel", userLevel, { expires: 1 });

      // Small delay to ensure storage is committed
      setTimeout(resolve, 100);
    });
  };

  const showToast = (title, description, status) => {
    toast({
      title,
      description,
      position: "top",
      status,
      isClosable: true,
    });
  };

  return (
    <GeneralLayout
      page_type="portal"
      pageContent={
        <Container
          as={SimpleGrid}
          maxW={"7xl"}
          columns={{ base: 1, md: 2 }}
          spacing={{ base: 10, lg: 32 }}
          py={{ base: "8em", sm: 40, md: 40 }}
          height={{ base: "auto", lg: "auto" }}
        >
          <Stack
            spacing={10}
            justifyContent={"center"}
            textAlign={{ base: "center", lg: "left" }}
          >
            <Heading
              color={useColorModeValue("blue.400", "gray.50")}
              fontSize={{ base: "2xl", sm: "3xl", md: "5xl" }}
            >
              To access your portal, please log in here
            </Heading>
          </Stack>
          <Stack
            rounded={"xl"}
            p={{ base: 4, sm: 6, md: 8 }}
            spacing={{ base: 8 }}
            maxW={{ lg: "lg" }}
            justifyContent={"center"}
            bg={useColorModeValue("blue.100", "blue.800")}
          >
            <Stack spacing={2}>
              <Heading
                textAlign={"center"}
                color={useColorModeValue("gray.800", "gray.50")}
              >
                Login
              </Heading>
            </Stack>
            <Box as={"form"} onSubmit={formik.handleSubmit}>
              <Stack spacing={2}>
                <CustomInput
                  placeholder="Email Address"
                  type="email"
                  py="7"
                  borderRadius="0"
                  name="email"
                  isInvalid={formik.errors.email && formik.touched.email}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  formErroMsg={formik.errors.email}
                />
                <CustomInput
                  placeholder="Password"
                  type={showPassword ? "text" : "password"}
                  isInvalid={formik.errors.password && formik.touched.password}
                  py="7"
                  borderRadius="0"
                  name="password"
                  onChange={formik.handleChange}
                  value={formik.values.password}
                  formErroMsg={formik.errors.password}
                />
                <HStack justify="space-between" my={"4"} display="flex">
                  <FormControl display="flex" alignItems="center" gap={3}>
                    <Switch
                      id="togglePassword"
                      type="switch"
                      label={showPassword ? "Hide Password" : "Show Password"}
                      checked={showPassword}
                      onChange={handleShowPassword}
                      colorScheme="green"
                    />
                    <FormLabel htmlFor="email-alerts" mb="0">
                      Show Password?
                    </FormLabel>
                  </FormControl>
                  <CustomButton
                    _hover={{ textDecoration: "underline" }}
                    as={"a"}
                    link="/forgot-password"
                    label=" Forgot password?"
                    variant="text"
                  />
                </HStack>
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <CustomButton
                    type="submit"
                    label="Login"
                    width={"full"}
                    bg={"blue.400"}
                    color={"white"}
                    _hover={{
                      bg: "blue.500",
                    }}
                    disabled={loading}
                  />
                )}
                <Stack>
                  <Text color="fg.muted" align={"center"}>
                    Didn't have an account?{" "}
                    <CustomButton
                      as={"a"}
                      link="/register"
                      label="Register"
                      variant="link"
                    />
                  </Text>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Container>
      }
    />
  );
}